import { useMemo } from 'react'
import { useCurrentUser } from '@cma/app'
import {
  CmaPermissionIdEnum,
  PlatformClientQuery,
  Brokerage,
  Office
} from '@cma/generated/graphql'

interface SelectedEntityLike {
  id: string
  brandableType: string
  [key: string]: unknown // Allow additional properties
}

type PlatformClientType = PlatformClientQuery['platformClient']

type UseBrandPermissionsReturn = {
  hasBrandPermissions: boolean
  hasOfficePermissionForSelected: (
    selectedEntity: SelectedEntityLike,
    platformClient?: PlatformClientType
  ) => boolean
  isSuperAdmin: boolean
  isOfficeAdmin: boolean
}

export function useBrandPermissions(): UseBrandPermissionsReturn {
  const { data: { currentUser } = {} } = useCurrentUser()

  return useMemo(() => {
    const userPermissions = currentUser?.permissions ?? []

    const brandingPermission = userPermissions.find(
      (permission) =>
        permission.permissionId === CmaPermissionIdEnum.CmaBrandingManage
    )

    const hasBrandPermissions = !!brandingPermission
    const isSuperAdmin = brandingPermission?.scope === 'A'
    const isOfficeAdmin = brandingPermission?.scope === 'O'

    const hasOfficePermission = (officeId: string): boolean => {
      if (!brandingPermission) {
        return false
      }

      if (brandingPermission.scope === 'A') {
        return true
      }

      if (brandingPermission.scope === 'S') {
        const officeIds = brandingPermission.officeIds || []
        return officeIds.includes(officeId)
      }

      if (brandingPermission.scope === 'O') {
        const officeIds = brandingPermission.officeIds || []
        return officeIds.includes(officeId)
      }

      return false
    }

    const hasTeamPermission = (
      teamId: string,
      platformClient?: PlatformClientType
    ): boolean => {
      if (!brandingPermission) {
        return false
      }

      if (brandingPermission.scope === 'A') {
        return true
      }

      if (platformClient?.teams) {
        const teamExists = platformClient.teams.some(
          (team) => team.id === teamId
        )

        const brandingExists = platformClient.brandings?.some(
          (branding) =>
            branding.brandableId === teamId && branding.brandableType === 'Team'
        )

        if (teamExists && brandingExists) {
          return true
        }
      }

      return false
    }

    const hasPermissionForAllOfficesInBrokerage = (
      brokerageId: string,
      platformClient?: PlatformClientType
    ): boolean => {
      if (isSuperAdmin) return true

      const brokerage = platformClient?.brokerages?.find(
        (b: Brokerage) => b.id === brokerageId
      )

      if (!brokerage) return false

      const officesInBrokerage = brokerage.offices || []

      if (officesInBrokerage.length === 0) return false

      const hasAccessToAllOffices = officesInBrokerage.every((office: Office) =>
        hasOfficePermission(office.id)
      )

      if (!hasAccessToAllOffices) return false

      const brokerageBranding = platformClient?.brandings?.find(
        (brandingItem) =>
          brandingItem.brandableId === brokerageId &&
          brandingItem.brandableType === 'Brokerage'
      )

      if (!brokerageBranding) return true

      return true
    }

    const hasOfficePermissionForSelected = (
      selectedEntity: SelectedEntityLike,
      platformClient?: PlatformClientType
    ): boolean => {
      if (isSuperAdmin) return true

      if (hasBrandPermissions) {
        switch (selectedEntity.brandableType) {
          case 'Account':
            return false

          case 'Office':
            return hasOfficePermission(selectedEntity.id)

          case 'Brokerage':
            return hasPermissionForAllOfficesInBrokerage(
              selectedEntity.id,
              platformClient
            )

          case 'Team':
            return hasTeamPermission(selectedEntity.id, platformClient)

          default:
            return false
        }
      }

      return false
    }

    return {
      hasBrandPermissions,
      isSuperAdmin,
      isOfficeAdmin,
      hasOfficePermissionForSelected
    }
  }, [currentUser?.permissions])
}
